/**
 * The storyboard — the product's whole reason for existing.
 *
 * The GM looks at this for a few seconds between scenes, so a branch's label
 * (the action gate: "if they open the reliquary") has to read first, because
 * that is what they match against what the players just did. Imagery is what
 * they recognise it by; the narrative is the detail they only read if the
 * table actually goes that way.
 *
 * Frames arrive from the API as one flat list per checkpoint and are grouped
 * by `branch_idx` here.
 */
import { useState } from 'react';
import type { Branch, Checkpoint, Frame } from '@/api/types';

interface Props {
  checkpoints: Checkpoint[];
  building: { title: string; message: string } | null;
  onOpenBranch: (checkpoint: Checkpoint, branchIdx: number) => void;
}

export function Storyboard({ checkpoints, building, onOpenBranch }: Props) {
  const [showEarlier, setShowEarlier] = useState(false);
  if (checkpoints.length === 0) {
    return building ? (
      <div className="empty">
        <span className="spinner" aria-hidden="true" />
        <h2 className="empty__title">{building.title}</h2>
        <p className="empty__body">{building.message}</p>
      </div>
    ) : (
      <div className="empty">
        <h2 className="empty__title">No storyboard yet</h2>
        <p className="empty__body">
          Start a session and turn on the mic. Branches appear once the table
          reaches a decision worth preparing for.
        </p>
      </div>
    );
  }

  return (
    <div>
      {(showEarlier ? checkpoints : checkpoints.slice(0, 1)).map(
        (checkpoint, index) => {
          const branches = branchesOf(checkpoint);
          // Newest first, so index 0 is the live one. Number them from the
          // session's start, which is the order they were actually prepared in.
          const number = checkpoints.length - index;
          const isLatest = index === 0;
          return (
            <section
              className="checkpoint"
              key={checkpoint.id}
              aria-label={`Checkpoint ${number}`}
            >
              <div className="checkpoint__meta">
                {/* Name the container explicitly. "3 branches" on its own reads
                  as "3 checkpoints have happened", which is the opposite of
                  what it means — they are alternatives within one moment. */}
                <strong>
                  {isLatest ? 'Latest checkpoint' : `Checkpoint ${number}`}
                </strong>
                {isLatest && checkpoints.length > 1 ? (
                  <span style={{ color: 'var(--ink-faint)' }}>
                    ({number} of {checkpoints.length})
                  </span>
                ) : null}
                <span aria-hidden="true">·</span>
                <span>{statusLabel(checkpoint)}</span>
                <span aria-hidden="true">·</span>
                <span>
                  {branches.length === 1
                    ? '1 way this could go'
                    : `${branches.length} ways this could go`}
                </span>
                <span aria-hidden="true">·</span>
                <span>{frameProgress(checkpoint)}</span>
              </div>

              {branches.length === 0 ? (
                <p style={{ color: 'var(--ink-faint)' }}>
                  Preparing storyboard…
                </p>
              ) : (
                <div className="branches">
                  {branches.map((branch, index) => (
                    <BranchCard
                      key={`${checkpoint.id}-${index}`}
                      branch={branch}
                      frames={framesFor(checkpoint, index)}
                      onOpen={() => onOpenBranch(checkpoint, index)}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        },
      )}
      {checkpoints.length > 1 ? (
        <button
          type="button"
          className="button button--quiet checkpoint-archive"
          aria-expanded={showEarlier}
          onClick={() => setShowEarlier((value) => !value)}
        >
          {showEarlier
            ? 'Hide earlier checkpoints'
            : `Earlier checkpoints (${checkpoints.length - 1})`}
        </button>
      ) : null}
    </div>
  );
}

function BranchCard({
  branch,
  frames,
  onOpen,
}: {
  branch: Branch;
  frames: Frame[];
  onOpen: () => void;
}) {
  const lead = frames.find((f) => f.url) ?? frames[0];
  const ready = frames.filter(isFrameReady).length;

  return (
    <button type="button" className="branch" onClick={onOpen}>
      {lead?.url ? (
        <img
          className="branch__image"
          src={lead.url}
          // The gate is what a screen-reader user needs, not "image".
          alt={`${branch.label}. ${branch.narrative}`}
          loading="lazy"
        />
      ) : (
        <FramePlaceholder frame={lead} />
      )}
      <span className="branch__body">
        <span className="branch__gate">{branch.label}</span>
        <span className="branch__title">{branch.narrative}</span>
        {frames.length > 0 ? (
          <span
            style={{
              display: 'block',
              marginTop: 'var(--space-2)',
              fontSize: '0.75rem',
              color: 'var(--ink-faint)',
            }}
          >
            {ready}/{frames.length} frames ready
          </span>
        ) : null}
      </span>
    </button>
  );
}

function FramePlaceholder({ frame }: { frame: Frame | undefined }) {
  const state = pendingFrameState(frame);
  return (
    <span
      className={
        state.active
          ? 'branch__placeholder branch__placeholder--active'
          : 'branch__placeholder'
      }
    >
      {state.active ? (
        <span className="branch__placeholder-silhouette" aria-hidden="true">
          <svg viewBox="0 0 320 200" preserveAspectRatio="xMidYMid slice">
            <circle
              className="branch__placeholder-moon"
              cx="268"
              cy="36"
              r="22"
            />
            <path
              className="branch__placeholder-ridge branch__placeholder-ridge--back"
              d="M0 156 52 98l47 47 49-66 63 70 47-47 62 57v41H0Z"
            />
            <path
              className="branch__placeholder-ridge branch__placeholder-ridge--front"
              d="M0 175 68 134l54 38 68-55 61 48 69-38v73H0Z"
            />
          </svg>
        </span>
      ) : null}
      <span className="branch__placeholder-status">
        {state.active ? (
          <span className="branch__placeholder-dot" aria-hidden="true" />
        ) : null}
        {state.label}
      </span>
      <strong className="branch__placeholder-title">
        {frame?.label || state.fallbackTitle}
      </strong>
    </span>
  );
}

export function branchesOf(checkpoint: Checkpoint): Branch[] {
  return (checkpoint.branches_json?.branches ?? []).map((branch) => ({
    ...branch,
    narrative: withoutDecisionMenu(branch.narrative, branch.label),
  }));
}

const DECISION_MENU_SUFFIX =
  /\s*(?:[—–-]\s*)?decision(?:\s+point)?\s*:\s*.*$/is;

export function withoutDecisionMenu(narrative: string, fallback = ''): string {
  const cleaned = narrative.replace(DECISION_MENU_SUFFIX, '').trim();
  return cleaned || fallback;
}

export function framesFor(checkpoint: Checkpoint, branchIdx: number): Frame[] {
  return (checkpoint.frames ?? [])
    .filter((f) => f.branch_idx === branchIdx)
    .sort((a, b) => a.frame_idx - b.frame_idx);
}

function frameProgress(checkpoint: Checkpoint): string {
  const frames = checkpoint.frames ?? [];
  if (frames.length === 0) return 'frames queued';
  const ready = frames.filter(isFrameReady).length;
  return `${ready}/${frames.length} frames ready`;
}

/**
 * A frame counts as ready once the agent is done with it.
 *
 * Not the same as "has an image": in playtest skip-render mode frames are
 * marked ready with the prompt as their content and never get a URL, and
 * counting only URLs reported those sessions as permanently 0/N.
 */
function isFrameReady(frame: Frame): boolean {
  return Boolean(frame.url) || frame.status === 'ready';
}

function statusLabel(checkpoint: Checkpoint): string {
  if (checkpoint.status === 'rendering' || checkpoint.status === 'ready') {
    return 'Current';
  }
  return checkpoint.status
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function pendingFrameState(frame: Frame | undefined): {
  active: boolean;
  label: string;
  fallbackTitle: string;
} {
  if (!frame) {
    return {
      active: true,
      label: 'Images queued',
      fallbackTitle: 'Artwork is next',
    };
  }

  switch (frame.status) {
    case 'queued':
      return {
        active: true,
        label: 'Queued to render',
        fallbackTitle: 'Scene ready for artwork',
      };
    case 'rendering':
      return {
        active: true,
        label: 'Rendering image',
        fallbackTitle: 'Bringing the scene into view',
      };
    case 'detecting':
      return {
        active: true,
        label: 'Checking key details',
        fallbackTitle: 'Finishing the image',
      };
    case 'failed':
      return {
        active: false,
        label: 'Image unavailable',
        fallbackTitle: 'The scene text is still ready',
      };
    default:
      // Playtest mode marks frames ready with the prompt as their content.
      return {
        active: false,
        label: 'Scene prompt ready',
        fallbackTitle: 'Artwork skipped for this preview',
      };
  }
}
